import React, { useEffect, useState } from "react";
import { CButton, CCard, CCardBody, CCardFooter, CCardHeader, CCol, CForm, CFormInput, CFormLabel, CFormSelect, CRow, CSpinner } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilArrowLeft, cilTrash } from "@coreui/icons";
import { useNavigate, useParams } from "react-router-dom";
import { insert, update, remove, getDetails } from "src/services/Stocks";
import { Toaster } from "src/components/Toaster";

function StockActions() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    id: 0,
    user_id: 0,
    product_id: 0,
    visitation_id: 0,
    store_id: 0,
    stock: 0,
  });

  const [loading, setLoading] = useState({ page: !!id, submit: false, delete: false });
  const [validated, setValidated] = useState(false);
  const [toaster, setToaster] = useState({ open: false, message: "", color: "success" });

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const res = await getDetails(id);
        const s = res.stock || {};
        setFormData({
          id: s.id || 0,
          user_id: s.user_id || 0,
          product_id: s.product_id || 0,
          visitation_id: s.visitation_id || 0,
          store_id: s.store_id || 0,
          stock: s.stock || 0,
        });
      } catch (e) {
        setToaster({ open: true, message: "Failed to load stock details", color: "danger" });
      } finally {
        setLoading((p) => ({ ...p, page: false }));
      }
    };
    load();
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(true);
      return;
    }

    setLoading((p) => ({ ...p, submit: true }));
    try {
      const payload = { ...formData, id: isEdit ? id : undefined };
      const resp = isEdit ? await update(payload) : await insert(payload);
      if (resp?.data?.success) {
        setToaster({ open: true, message: resp.data.message || (isEdit ? "Stock updated" : "Stock created"), color: "success" });
        setTimeout(() => navigate(-1), 1200);
      } else {
        throw new Error(resp?.data?.message || "Operation failed");
      }
    } catch (err) {
      setToaster({ open: true, message: err.message || "Error", color: "danger" });
    } finally {
      setLoading((p) => ({ ...p, submit: false }));
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Hapus data stok ini?")) return;

    setLoading((p) => ({ ...p, delete: true }));
    try {
      const resp = await remove(id);
      if (resp?.data?.success) {
        setToaster({ open: true, message: resp.data.message || "Stock deleted", color: "success" });
        setTimeout(() => navigate("/stocks"), 1200);
      } else {
        throw new Error(resp?.data?.message || "Delete failed");
      }
    } catch (err) {
      setToaster({ open: true, message: err.message || "Error deleting", color: "danger" });
    } finally {
      setLoading((p) => ({ ...p, delete: false }));
    }
  };

  if (loading.page) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  return (
    <CCard>
      <Toaster open={toaster.open} message={toaster.message} setOpen={(v) => setToaster((p) => ({ ...p, open: v }))} color={toaster.color} />
      <CCardHeader>
        <CRow className="align-items-center">
          <CCol md={6}>
            <h5>{isEdit ? "Edit Stock" : "Add Stock"}</h5>
          </CCol>
          <CCol md={6} className="text-end">
            <CButton color="light" onClick={() => navigate(-1)} disabled={loading.submit || loading.delete}>
              <CIcon icon={cilArrowLeft} className="me-2" />
              Back
            </CButton>
          </CCol>
        </CRow>
      </CCardHeader>
      <CCardBody>
        <CForm onSubmit={handleSubmit} noValidate validated={validated}>
          <div style={{ margin: 16 }}>
            <CRow className="mb-3">
              <CCol md={3}><CFormLabel>User ID</CFormLabel></CCol>
              <CCol>
                <CFormInput type="number" name="user_id" value={formData.user_id} onChange={handleChange} />
              </CCol>
            </CRow>
            <CRow className="mb-3">
              <CCol md={3}><CFormLabel>Product ID</CFormLabel></CCol>
              <CCol>
                <CFormInput type="number" name="product_id" value={formData.product_id} onChange={handleChange} required />
              </CCol>
            </CRow>
            <CRow className="mb-3">
              <CCol md={3}><CFormLabel>Visitation ID</CFormLabel></CCol>
              <CCol>
                <CFormInput type="number" name="visitation_id" value={formData.visitation_id} onChange={handleChange} />
              </CCol>
            </CRow>
            <CRow className="mb-3">
              <CCol md={3}><CFormLabel>Store ID</CFormLabel></CCol>
              <CCol>
                <CFormInput type="number" name="store_id" value={formData.store_id} onChange={handleChange} required />
              </CCol>
            </CRow>
            <CRow className="mb-3">
              <CCol md={3}><CFormLabel>Stock</CFormLabel></CCol>
              <CCol>
                <CFormInput type="number" name="stock" value={formData.stock} onChange={handleChange} required />
              </CCol>
            </CRow>
          </div>
          <CCardFooter className="text-end">
            {isEdit && (
              <CButton color="danger" className="me-2" onClick={handleDelete} disabled={loading.submit || loading.delete}>
                {loading.delete ? <CSpinner size="sm" /> : (<><CIcon icon={cilTrash} className="me-2" />Delete</>)}
              </CButton>
            )}
            <CButton color="success" type="submit" disabled={loading.submit || loading.delete}>
              {loading.submit ? <CSpinner size="sm" /> : isEdit ? "Update" : "Create"}
            </CButton>
          </CCardFooter>
        </CForm>
      </CCardBody>
    </CCard>
  );
}

export default StockActions;

